import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import ResizeModule from "quill-resize-image";
import "./RichTextEditor.css";

// ---------------------------------------------------------------------------
// Font whitelist — exposed to the toolbar dropdown. Values are full
// `font-family` stacks (used by Quill's style-based attributor below) so the
// emitted HTML has inline `style="font-family: …"`, which survives once the
// email leaves our app and reaches recipients' inboxes.
// ---------------------------------------------------------------------------
const FONT_OPTIONS = [
  { value: "Arial, sans-serif", label: "Arial" },
  { value: "Helvetica, Arial, sans-serif", label: "Helvetica" },
  { value: "Calibri, \"Segoe UI\", sans-serif", label: "Calibri" },
  { value: "Cambria, Georgia, serif", label: "Cambria" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "Tahoma, Geneva, sans-serif", label: "Tahoma" },
  { value: "\"Times New Roman\", Times, serif", label: "Times New Roman" },
  { value: "\"Trebuchet MS\", sans-serif", label: "Trebuchet MS" },
  { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
  { value: "\"Courier New\", Courier, monospace", label: "Courier New" },
  { value: "Consolas, \"Lucida Console\", monospace", label: "Consolas" },
];
const FONT_WHITELIST = FONT_OPTIONS.map((f) => f.value);

const SIZE_WHITELIST = ["10px", "12px", "14px", "16px", "18px", "24px", "32px"];

// Register custom font + size attributes ONCE per page-load. We use the
// STYLE-based attributors so Quill emits inline `style="font-family: …"`
// and `style="font-size: …"` on the HTML. That matters because the
// rendered HTML is sent to recipients verbatim via Microsoft Graph — their
// email clients don't have our `.ql-font-*` stylesheet, but they do
// honour inline style attributes.
const FontStyle = Quill.import("attributors/style/font");
FontStyle.whitelist = FONT_WHITELIST;
Quill.register(FontStyle, true);

const SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = SIZE_WHITELIST;
Quill.register(SizeStyle, true);

// Inline resize / move handles for embedded images. Adds click-to-select +
// corner-drag-to-resize on `<img>` tags. Emits inline `width`/`height`
// attributes so the resized dimensions survive the trip through Microsoft
// Graph into recipients' inboxes.
Quill.register("modules/resize", ResizeModule);

// Inject per-option label styling so the font and size dropdowns preview
// each option using its own face/size. Done in JS (rather than the .css
// file) because the picker `data-value` for font is the full font-family
// stack and would be awkward to write as a stable CSS selector list.
if (typeof document !== "undefined" && !document.getElementById("rte-font-picker-styles")) {
  const style = document.createElement("style");
  style.id = "rte-font-picker-styles";
  const fontCss = FONT_OPTIONS.map((f) => {
    const sel = f.value.replace(/"/g, '\\"');
    return `
      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="${sel}"]::before,
      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="${sel}"]::before {
        content: "${f.label}";
        font-family: ${f.value};
      }`;
  }).join("\n");
  // Size dropdown — Quill's default labels show "Normal" for every size,
  // which is confusing. Replace with the actual px value so users see
  // "10 px / 12 px / …".
  const sizeCss = SIZE_WHITELIST.map((sz) => `
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="${sz}"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="${sz}"]::before {
        content: "${sz}";
        font-size: ${sz};
      }`).join("\n");
  // Default label when no size is set.
  const sizeDefault = `
      .ql-snow .ql-picker.ql-size .ql-picker-label:not([data-value])::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item:not([data-value])::before {
        content: "Size";
      }`;
  style.textContent = fontCss + "\n" + sizeCss + "\n" + sizeDefault;
  document.head.appendChild(style);
}

const TOOLBAR = [
  [{ font: FONT_WHITELIST }, { size: SIZE_WHITELIST }],
  [{ header: [false, 1, 2, 3] }],
  ["bold", "italic", "underline"],
  [{ color: [] }, { background: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ align: [] }],
  ["link", "image", "blockquote"],
  ["clean"],
];

/**
 * Outlook-style rich text editor with imperative `insertAtCursor` method
 * for placeholder insertion. Returns HTML via onChange.
 */
const RichTextEditor = forwardRef(({ value, onChange, placeholder, testId }, ref) => {
  const quillRef = useRef(null);

  const modules = useMemo(() => ({
    toolbar: TOOLBAR,
    clipboard: { matchVisual: false },
    resize: {
      // Show numeric size in the resize handle for clarity.
      showSize: true,
      // Embed resize result as inline width/height so styles survive email.
      locale: { altTip: "Hold Alt to drag freely", floatLeft: "Left", floatRight: "Right", center: "Center", restore: "Restore" },
    },
  }), []);

  useImperativeHandle(ref, () => ({
    insertAtCursor: (text) => {
      const editor = quillRef.current?.getEditor?.();
      if (!editor) return;
      const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
      editor.insertText(range.index, text, "user");
      editor.setSelection(range.index + text.length, 0, "user");
      editor.focus();
    },
    focus: () => quillRef.current?.getEditor?.()?.focus?.(),
    getEditor: () => quillRef.current?.getEditor?.(),
  }));

  return (
    <div data-testid={testId} className="rich-text-editor-wrapper">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        modules={modules}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder || "Write your email…"}
      />
    </div>
  );
});

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
